















































import {Component, Prop, Vue} from "vue-property-decorator";
import { Inject } from "vue-typedi";
import { ProductService } from "@/services/ProductService";
import { StoreService } from "@/services/StoreService";
import { EventBus } from "@/helpers/EventBus";
import { Product } from "@/model/product/Product";
import { Zone } from "@/model/Zone";
import CapacityProductPanel from "@/components/product-management/CapacityProductPanel.vue";
import ErrorDialog from "@/components/dialog-box/ErrorDialog.vue";
import ConfirmDialog from "@/components/dialog-box/ConfirmDialog.vue";
import { SalesCapacity } from "@/model/product/SalesCapacity";
import dayjs from "dayjs";
import { TraceabilityContextConstants } from "@/model/product/TraceabilityContextConstants";
import { Traceability } from "@/model/product/Traceability";
import {PreSelectedBulkMenu} from "@/model/product/PreSelectedBulkMenu";

@Component({
    components: { CapacityProductPanel, ErrorDialog, ConfirmDialog },
})
export default class ZoneSettingsForm extends Vue {
    @Inject()
    storeService!: StoreService;

    @Inject()
    productService!: ProductService;

    @Prop({})
    public preSelectedBulkMenu: PreSelectedBulkMenu;

    public selectedZone: Zone = null;
    public selectedSubMarket: string = null;

    public allProducts: Product[] = [];
    public productsToDisplay: Product[] = [];
    public productToChange: Product = null;

    public allZones: Zone[] = [];
    public allSubMarkets: string[] = [];

    public traceability: Traceability = null;
    public newOrigin: string = null;

    public searchTerm: string = null;
    public keyToUpdate: number = 0;

    public hasError: boolean = false;

    public confirmOutOfStockDialogOpen: boolean = false;
    public messageOutOfStock: string = "Confirmez-vous la mise en rupture du produit ce jour ?";
    public titleOutOfStock: string = "Mise en rupture";

    public confirmOriginDialogOpen: boolean = false;
    public messageOrigin: string = "";
    public titleOrigin: string = "Changement d'origine";

    created() {
        EventBus.$on(EventBus.REFRESH_STORE, this.onRefreshStore);
        EventBus.$on(EventBus.UPDATE_PRODUCT_OUT_OF_STOCK_STATUS, this.onRefreshOutOfStockStatus);
        EventBus.$on(EventBus.UPDATE_ORIGIN, this.onRefreshProductOrigin);
        this.storeService.refreshStore();
        this.productService.getTraceability(TraceabilityContextConstants.ALL).then(res => {
            this.traceability = res;
        });
    }

    beforeDestroy() {
        EventBus.$off(EventBus.REFRESH_STORE, this.onRefreshStore);
        EventBus.$off(EventBus.UPDATE_PRODUCT_OUT_OF_STOCK_STATUS, this.onRefreshOutOfStockStatus);
        EventBus.$off(EventBus.UPDATE_ORIGIN, this.onRefreshProductOrigin);
    }

    onRefreshOutOfStockStatus(product: Product) {
        if (!product) {
            return;
        }
        this.productToChange = product;
        this.confirmOutOfStockDialogOpen = true;
    }

    updateOutOfStockStatus() {
        this.confirmOutOfStockDialogOpen = false;
        this.productService.updateProductOutOfStockStatus(this.selectedZone.zoneCode, this.productToChange.cug)
            .then(productModified => {
                this.updateProduct(productModified);
            })
            .catch(error => {
                this.hasError = true;
            });
    }


    onRefreshProductOrigin(origin: string, product: Product) {
        if (!origin) {
            return;
        }
        this.productToChange = product;
        this.newOrigin = origin;
        this.setupConfirmOriginUpdatePopUp(product.origin, this.newOrigin);
        this.confirmOriginDialogOpen = true;
    }

    updateProductOrigin() {
        this.confirmOriginDialogOpen = false;
        this.productService.updateProductOrigin(this.newOrigin, this.selectedZone.zoneCode, this.productToChange.cug)
            .then(productUpdated => {
                this.updateProduct(productUpdated);
            })
            .catch(error => {
                this.hasError = true;
            });
    }

    private onRefreshStore() {
        this.retrieveZones();
        this.applyPreSelection();
        EventBus.$off(EventBus.REFRESH_STORE, this.onRefreshStore);
    }

    private retrieveZones() {
        this.allZones = this.storeService.currentZones.filter(zone => zone.enabled);
    }

    private applyPreSelection() {
        if (this.preSelectedBulkMenu.zoneLabel == null) {
            return;
        }
        const zone = this.allZones.find(z => z.zoneLabel === this.preSelectedBulkMenu.zoneLabel);
        if (zone == null) {
            return;
        }
        this.selectedZone = zone;

      this.retrieveProducts(zone).then(() => {
        if (this.preSelectedBulkMenu.subMarketLabel != null && this.allSubMarkets.includes(this.preSelectedBulkMenu.subMarketLabel)) {
          this.selectedSubMarket = this.preSelectedBulkMenu.subMarketLabel;
          this.updateProductsToDisplay();
        }
      });
    }

    onZoneChange() {
        this.selectedSubMarket = null;
        this.allSubMarkets = [];
        this.allProducts = [];
        this.productsToDisplay = [];

        if (this.selectedZone == null) {
            this.selectedSubMarket = null;
            return;
        }

        this.retrieveProducts(this.selectedZone);
    }

    onSubMarketChange() {
        if (this.selectedSubMarket == null) {
            this.productsToDisplay = [];
            return;
        }
        this.$emit("onSubMarketChange", this.selectedSubMarket, this.selectedZone.zoneLabel);
        this.updateProductsToDisplay();
    }

    retrieveProducts(zone: Zone) {
        if (zone == null) {
            return Promise.resolve();
        }

        return this.productService.getProducts(zone.zoneCode)
            .then(products => {
                this.allProducts = products;
                this.populateSubMarkets();
            });
    }

    populateSubMarkets() {
        if (this.allProducts.length === 0) {
            return;
        }
        this.allSubMarkets = [...new Set(
            this.allProducts
                .filter(product => product.sectorLabel)
                .map(product => product.sectorLabel)
        )];
    }

    hasSubMarkets(): boolean {
        return this.allSubMarkets.length > 0;
    }

    updateProductsToDisplay() {
        this.productsToDisplay = this.allProducts
            .filter((product: Product) => product != null && product.sectorLabel === this.selectedSubMarket)
            .filter((product: Product) => this.isMatchingSearchTerms(product))
            .sort((productA: Product, productB: Product) => this.getDayCapacityQuantity(productA) - this.getDayCapacityQuantity(productB))
            .sort((product: Product) => this.isOutOfStock(product.lastOutOfStockDate) ? 1 : -1);

        this.keyToUpdate++;
    }

    private isMatchingSearchTerms(product: Product): boolean {
        if (product == null) {
            return false;
        }

        if (this.searchTerm == null){
            return true;
        }

        const cleanSearchTerm = this.normalizeText(this.searchTerm);
        if (product.cug.includes(cleanSearchTerm)) {
            return true;
        }

        if (this.normalizeText(product.label).includes(cleanSearchTerm)) {
            return true;
        }

        return false;
    }

    private normalizeText(text: string): string {
        return text?.trim()
                    .toLocaleLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "");
    }

    get canSearchProducts(): boolean {
        return this.allProducts != null && this.allProducts.length > 0 && this.selectedSubMarket != null;
    }

    updateProduct(productModified: Product) {
        this.allProducts = this.allProducts.map(product => {
            if (product.productId === productModified.productId) {
                return productModified;
            }
            return product;
        })
        this.updateProductsToDisplay();
    }

    getProductById(productId: string): Product {
        if (!productId) {
            return null;
        }

        return this.allProducts.find(product => product.productId == productId);
    }

    onClickReturn() {
        this.$emit("onReturnSearchPage");
    }

    setupConfirmOriginUpdatePopUp(currentOrigin: string, newOrigin: string) {
        this.messageOrigin = "Confirmez vous le changement d'origine du produit de " + currentOrigin + " à " + newOrigin + " ?"
    }

    closeErrorPopUp() {
        this.hasError = false;
    }

    closePopUp() {
        this.confirmOriginDialogOpen = false;
        this.confirmOutOfStockDialogOpen = false;
        this.updateProductsToDisplay();
    }

    getDayCapacityQuantity(product: Product) {
        if (product.weeklyCapacity == null) {
            return 0;
        }
        const dayCapacity: SalesCapacity = this.getDayCapacity(product);
        if (dayCapacity == null || (dayCapacity.quantity == null && !dayCapacity.isUnlimited)) {
            return -1;
        }
        if(dayCapacity.isUnlimited){
            return 1;
        }
        return dayCapacity.quantity;
    }

    getDayCapacity(product: Product): SalesCapacity{
        return product.weeklyCapacity.find(x => x.dayOfWeek.toUpperCase() === dayjs().format('dddd').toUpperCase());
    }

    isOutOfStock(outOfStockDate: Date) {
        return dayjs(outOfStockDate).isSame(dayjs(), 'day');
    }

}
